import type { ReactNode } from "react";

import { AnglerfishMark } from "./ui/AnglerfishMark";
import { useMediaQuery } from "../lib/useMediaQuery";

const BUILD_HASH = (import.meta.env.VITE_BUILD_HASH as string | undefined) ?? "dev";

/**
 * Auth shell. Two-pane: a refined brand panel on the left (BlueLake
 * Infosec identity, editorial pull-quote, build/region footnote), and a
 * sign-in form on the right. Boardroom aesthetic — no scope rings, no
 * scanlines, no military ornaments.
 */
export function SplitAuthLayout({ children }: { children: ReactNode }) {
  const isNarrow = useMediaQuery("(max-width: 879px)");

  return (
    <div style={styles.shell}>
      <div
        style={{
          ...styles.split,
          gridTemplateColumns: isNarrow ? "1fr" : "1.1fr 1fr",
        }}
      >
        <BrandPanel isNarrow={isNarrow} />
        <section
          style={{
            ...styles.form,
            padding: isNarrow ? "32px 24px" : styles.form.padding,
          }}
        >
          {children}
        </section>
      </div>
      <FooterMeta />
    </div>
  );
}

function BrandPanel({ isNarrow }: { isNarrow: boolean }) {
  return (
    <aside
      style={{
        ...styles.brand,
        padding: isNarrow ? "32px 24px" : styles.brand.padding,
      }}
    >
      {/* Top: brand lockup */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <AnglerfishMark size={30} color="var(--text-primary)" />
        <div style={{ lineHeight: 1.15 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 600,
              color: "var(--text-primary)",
              letterSpacing: "-0.018em",
            }}
          >
            Anglerfish
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              marginTop: 2,
            }}
          >
            BlueLake Infosec
          </div>
        </div>
      </div>

      {/* Middle: editorial pull-quote */}
      <div style={{ maxWidth: 380 }}>
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: 34,
            fontWeight: 500,
            letterSpacing: "-0.024em",
            lineHeight: 1.12,
            color: "var(--text-primary)",
          }}
        >
          The voice agent
          <br />
          <em style={{ color: "var(--accent)", fontStyle: "italic" }}>that dials.</em>
        </h2>
        <p
          style={{
            margin: "20px 0 0",
            fontSize: 14.5,
            color: "var(--text-secondary)",
            lineHeight: 1.6,
            letterSpacing: "-0.005em",
          }}
        >
          Authorized red-team operations. Engagement authoring, persona
          orchestration, halt control, and evidence-grade audit chain — built
          for senior infosec teams.
        </p>
      </div>

      {/* Bottom: signed footnote */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 28,
          paddingTop: 24,
          borderTop: "1px solid var(--border-subtle)",
          fontSize: 12,
        }}
      >
        <FootCell label="Build" value={BUILD_HASH} mono />
        <FootCell label="Region" value="us-west-2" />
        <FootCell label="Environment" value="Production" />
      </div>
    </aside>
  );
}

function FootCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
          fontSize: 12.5,
          color: "var(--text-primary)",
          fontWeight: 500,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function FooterMeta() {
  return (
    <div
      style={{
        marginTop: 24,
        display: "flex",
        gap: 20,
        alignItems: "center",
        fontSize: 12,
        color: "var(--text-muted)",
      }}
    >
      <span>© BlueLake Infosec — Authorized personnel only</span>
    </div>
  );
}

const styles = {
  shell: {
    // dvh keeps the auth shell off the mobile URL bar; the vh value is the
    // floor for engines that don't understand dvh (they ignore the unknown
    // unit on minHeight and use the height floor instead).
    minHeight: "100dvh",
    height: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "44px 20px",
    fontFamily: "var(--font-sans)",
    color: "var(--text-primary)",
    background: "var(--bg-primary)",
  },
  split: {
    width: "100%",
    maxWidth: 1080,
    minHeight: 560,
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-card)",
    background: "var(--bg-card)",
    boxShadow: "var(--shadow-lg)",
    overflow: "hidden",
  } as const,
  brand: {
    position: "relative" as const,
    padding: "44px 40px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "space-between",
    gap: 32,
    background: "var(--bg-secondary)",
    borderRight: "1px solid var(--border-subtle)",
  },
  form: {
    padding: "48px 44px",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    background: "var(--bg-card)",
  } as const,
};
